
import { Component, Vue } from 'vue-property-decorator'
import { AdminApi } from '@/wwapi/apis/admin-api'
import { cloneDeep } from 'lodash'
import { Form } from 'element-ui'

interface INavigationConfigData {
  id?: number
  region: string
  straightClockDirections: number[]
  minDistanceThreshold: number
  enableSameStreetCheck: boolean
  sameStreetMaxAngle: number
  enableStepSplitting: boolean
  isActive: boolean
}

const defaultData: INavigationConfigData = {
  region: '',
  straightClockDirections: [10, 11, 12, 1, 2],
  minDistanceThreshold: 15,
  enableSameStreetCheck: true,
  sameStreetMaxAngle: 90,
  enableStepSplitting: true,
  isActive: true
}

@Component({
  name: 'NavigationConfigList'
})
export default class extends Vue {
  private list: INavigationConfigData[] = []
  private listLoading = true
  private dialogFormVisible = false
  private dialogStatus = ''
  private deleteConfirmationVisible = false
  private deleteItemId: number | null = null
  private tempData: INavigationConfigData = cloneDeep(defaultData)

  private clockHours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

  private rules = {
    region: [
      { required: true, message: 'Region is required', trigger: 'change' }
    ],
    straightClockDirections: [
      { required: true, message: 'At least one direction is required', trigger: 'change', type: 'array', min: 1 }
    ]
  }

  created() {
    this.fetchData()
  }

  private getClockDescription(hour: number): string {
    const descriptions: Record<number, string> = {
      12: '0° straight',
      1: '30° slight right',
      2: '60° right',
      3: '90° hard right',
      4: '120°',
      5: '150°',
      6: '180° U-turn',
      7: '210°',
      8: '240°',
      9: '270° hard left',
      10: '300° left',
      11: '330° slight left'
    }
    return descriptions[hour] || ''
  }

  private async fetchData() {
    this.listLoading = true
    try {
      const adminApi = new AdminApi()
      const response = await adminApi.getNavigationConfigs()
      if (response.data.data) {
        this.list = response.data.data
      } else {
        this.list = []
      }
    } catch (error) {
      console.error('Error fetching navigation configs:', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load navigation configurations',
        type: 'error',
        duration: 2000
      })
      this.list = []
    } finally {
      this.listLoading = false
    }
  }

  showDeleteConfirmation(row: INavigationConfigData) {
    this.deleteItemId = row.id!
    this.deleteConfirmationVisible = true
  }

  async deleteItem() {
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.deleteNavigationConfig(this.deleteItemId!)
      if (data.status.code === 'success') {
        const index = this.list.findIndex((v: INavigationConfigData) => v.id === this.deleteItemId)
        this.list.splice(index, 1)
        this.$notify({
          title: 'Success',
          message: 'Navigation config deleted successfully',
          type: 'success',
          duration: 2000
        })
      }
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.response?.data?.status?.message || 'Failed to delete navigation config',
        type: 'error',
        duration: 2000
      })
    } finally {
      this.deleteItemId = null
      this.deleteConfirmationVisible = false
    }
  }

  private handleUpdate(row: INavigationConfigData) {
    this.tempData = cloneDeep(row)
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private updateData() {
    const adminApi = new AdminApi()
    ;(this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const { data } = await adminApi.updateNavigationConfig(
            this.tempData.id!,
            {
              region: this.tempData.region,
              straightClockDirections: this.tempData.straightClockDirections,
              minDistanceThreshold: this.tempData.minDistanceThreshold,
              enableSameStreetCheck: this.tempData.enableSameStreetCheck,
              sameStreetMaxAngle: this.tempData.sameStreetMaxAngle,
              enableStepSplitting: this.tempData.enableStepSplitting,
              isActive: this.tempData.isActive
            }
          )
          if (data.status.code === 'success') {
            const index = this.list.findIndex((v: INavigationConfigData) => v.id === data.data.id)
            this.list.splice(index, 1, data.data)
            this.dialogFormVisible = false
            this.$notify({
              title: 'Success',
              message: 'Navigation config updated successfully',
              type: 'success',
              duration: 2000
            })
          }
        } catch (error: any) {
          this.$notify({
            title: 'Error',
            message: error.response?.data?.status?.message || 'Failed to update navigation config',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
  }

  private handleCreate() {
    this.tempData = cloneDeep(defaultData)
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private createData() {
    const adminApi = new AdminApi()
    ;(this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const { data } = await adminApi.createNavigationConfig({
            region: this.tempData.region,
            straightClockDirections: this.tempData.straightClockDirections,
            minDistanceThreshold: this.tempData.minDistanceThreshold,
            enableSameStreetCheck: this.tempData.enableSameStreetCheck,
            sameStreetMaxAngle: this.tempData.sameStreetMaxAngle,
            enableStepSplitting: this.tempData.enableStepSplitting,
            isActive: this.tempData.isActive
          })
          if (data.status.code === 'success') {
            this.list = [...this.list, data.data]
            this.dialogFormVisible = false
            this.$notify({
              title: 'Success',
              message: 'Navigation config created successfully',
              type: 'success',
              duration: 2000
            })
          }
        } catch (error: any) {
          this.$notify({
            title: 'Error',
            message: error.response?.data?.status?.message || 'Failed to create navigation config',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
  }

  private async toggleStatus(row: INavigationConfigData, isActive: boolean) {
    try {
      const adminApi = new AdminApi()
      const { data } = await adminApi.updateNavigationConfig(row.id!, {
        region: row.region,
        straightClockDirections: row.straightClockDirections,
        minDistanceThreshold: row.minDistanceThreshold,
        enableSameStreetCheck: row.enableSameStreetCheck,
        sameStreetMaxAngle: row.sameStreetMaxAngle,
        enableStepSplitting: row.enableStepSplitting,
        isActive: isActive
      })
      if (data.status.code === 'success') {
        row.isActive = isActive
        const index = this.list.findIndex((v: INavigationConfigData) => v.id === row.id)
        this.list.splice(index, 1, row)
        this.$notify({
          title: 'Success',
          message: 'Status updated successfully',
          type: 'success',
          duration: 2000
        })
      }
    } catch (error: any) {
      this.$notify({
        title: 'Error',
        message: error.response?.data?.status?.message || 'Failed to update status',
        type: 'error',
        duration: 2000
      })
    }
  }
}
